<template>
  <div class="app-container postage-recharge-detail">
    <BjDivider>
      {{ $t('billingPage.PostageRecharge') }}
      <template #right>
        <el-button @click="handleBack">{{ $t('backText') }}</el-button>
        <el-button type="primary" icon="el-icon-download" :disabled="!fileName" @click="downloadFile">
          {{ $t('downloadText') }}
        </el-button>
      </template>
    </BjDivider>

    <el-row :gutter="20" class="detail-overview">
      <el-col class="detail-overview__col" :span="16">
        <div class="overview-card">
          <div class="overview-card__meta">{{ $t('billingPage.PostageRechargeInvoiceOverview') }}</div>
          <div class="overview-card__title">{{ detail.invoiceNumber || invoiceNumber }}</div>
          <div class="overview-card__sub">
            <span>{{ logisticsCompany }}</span>
          </div>
          <div class="overview-metrics">
            <div v-for="item in overviewMetrics" :key="item.key" class="overview-metrics__item" :class="item.className">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
        </div>
      </el-col>
      <el-col class="detail-overview__col" :span="8">
        <div class="reason-card">
          <div class="reason-card__label">{{ $t('billingPage.PostageRechargeUnmatchedAmount') }}</div>
          <div class="reason-card__amount">{{ formatMoney(detail.unmatchedAmount) }}</div>
          <el-form label-position="top">
            <el-form-item :label="$t('billingPage.PostageRechargeUnmatchedReason')">
              <el-input
                v-model="unmatchedReason"
                type="textarea"
                :rows="4"
                :placeholder="$t('billingPage.PostageRechargeReasonPlaceholder')"
              />
            </el-form-item>
          </el-form>
          <el-button type="primary" size="mini" :loading="reasonSaving" @click="saveReason">
            {{ $t('billingPage.PostageRechargeSaveReason') }}
          </el-button>
        </div>
      </el-col>
    </el-row>

    <BjDivider>
      {{ $t('billingPage.PostageRechargeMatchedOrders') }}
      <span class="divider-count">({{ filteredMatchedRows.length }})</span>
    </BjDivider>
    <div class="table-filter">
      <el-input
        v-model.trim="matchedSearchKeyword"
        class="table-filter__input"
        clearable
        prefix-icon="el-icon-search"
        :placeholder="$t('billingPage.PostageRechargeSearchOrderPlaceholder')"
        @input="handleMatchedSearch"
      />
    </div>
    <el-table
      :data="matchedPagedRows"
      border
      max-height="480px"
      :cell-class-name="getMatchedCellClass"
      @sort-change="handleMatchedSortChange"
    >
      <el-table-column
        prop="orderNumber"
        :label="$t('billingPage.PostageRechargeOrderNumber')"
        fixed="left"
        align="center"
        min-width="180"
      >
        <template slot-scope="scope">
          <button type="button" class="copy-value" @click="copyValue(scope.row.orderNumber)">
            {{ scope.row.orderNumber }} <i class="el-icon-copy-document" />
          </button>
        </template>
      </el-table-column>
      <el-table-column
        prop="trackingNumber"
        :label="$t('billingPage.PostageRechargeTrackingNumber')"
        fixed="left"
        align="center"
        min-width="170"
      >
        <template slot-scope="scope">
          <button type="button" class="copy-value" @click="copyValue(scope.row.trackingNumber)">
            {{ scope.row.trackingNumber }} <i class="el-icon-copy-document" />
          </button>
        </template>
      </el-table-column>
      <el-table-column
        v-for="column in matchedAmountColumns"
        :key="column.prop"
        :prop="column.prop"
        :min-width="column.width"
        align="center"
        sortable="custom"
      >
        <template v-slot:header>
          <span>{{ column.label }}</span>
          <el-tooltip v-if="column.formula" effect="dark" placement="top">
            <div slot="content" class="formula-tooltip">
              <strong>{{ $t(formulaDefinitions[column.formula].labelKey) }}</strong><br />
              {{ $t(formulaDefinitions[column.formula].descriptionKey) }}<br /><br />
              {{ $t(formulaDefinitions[column.formula].formulaKey) }}
            </div>
            <i class="el-icon-info formula-info" tabindex="0" />
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <el-popover
            v-if="column.prop === 'currentChargeAmount'"
            placement="bottom-end"
            width="320"
            trigger="click"
          >
            <div class="calculation-breakdown">
              <div class="calculation-breakdown__title">
                {{ $t('billingPage.PostageRechargeCalculationBreakdown') }}
              </div>
              <div class="calculation-breakdown__row">
                <span>{{ $t('billingPage.PostageRechargeCurrentDifference') }}</span>
                <strong>{{ formatMoney(scope.row.currentDifference) }}</strong>
              </div>
              <div class="calculation-breakdown__row">
                <span>{{ $t('billingPage.PostageRechargeProfitMargin') }}</span>
                <strong>{{ formatPercent(scope.row.profitMargin) }}</strong>
              </div>
              <div class="calculation-breakdown__result">
                <span>{{ $t('billingPage.PostageRechargeCurrentChargeAmount') }}</span>
                <strong>{{ formatMoney(scope.row.currentChargeAmount) }}</strong>
              </div>
            </div>
            <button
              slot="reference"
              type="button"
              class="amount-button"
              @mouseenter="setActiveFormula(scope.row, column.prop)"
              @mouseleave="clearActiveFormula"
              @focus="setActiveFormula(scope.row, column.prop)"
              @blur="clearActiveFormula"
            >
              {{ formatMoney(scope.row[column.prop]) }}
            </button>
          </el-popover>
          <span
            v-else
            class="detail-amount"
            @mouseenter="column.formula && setActiveFormula(scope.row, column.prop)"
            @mouseleave="column.formula && clearActiveFormula()"
          >
            {{ formatMoney(scope.row[column.prop]) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="profitMargin" min-width="130" align="center" sortable="custom">
        <template v-slot:header>
          <span>{{ $t('billingPage.PostageRechargeProfitMargin') }}</span>
          <el-tooltip effect="dark" placement="top">
            <div slot="content" class="formula-tooltip">
              <strong>{{ $t(formulaDefinitions.profitMargin.labelKey) }}</strong><br />
              {{ $t(formulaDefinitions.profitMargin.descriptionKey) }}<br /><br />
              {{ $t(formulaDefinitions.profitMargin.formulaKey) }}
            </div>
            <i class="el-icon-info formula-info" tabindex="0" />
          </el-tooltip>
        </template>
        <template slot-scope="scope">
          <span
            class="detail-amount"
            @mouseenter="setActiveFormula(scope.row, 'profitMargin')"
            @mouseleave="clearActiveFormula"
          >
            {{ formatPercent(scope.row.profitMargin) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.operationText')" fixed="right" align="center" width="90">
        <template slot-scope="scope">
          <el-button type="text" @click="showOrderInfo(scope.row)">{{ $t('viewText') }}</el-button>
        </template>
      </el-table-column>
    </el-table>
    <pagination
      v-if="filteredMatchedRows.length"
      class="table-pagination"
      :total="filteredMatchedRows.length"
      :page.sync="matchedPage"
      :limit.sync="matchedPageSize"
      :page-sizes="pageSizes"
      :auto-scroll="false"
      @pagination="handleMatchedPagination"
    />

    <BjDivider>
      {{ $t('billingPage.PostageRechargeUnmatchedItems') }}
      <span class="divider-count is-danger">({{ unmatchedRows.length }})</span>
    </BjDivider>
    <el-table :data="unmatchedPagedRows" border max-height="420px" @sort-change="handleUnmatchedSortChange">
      <el-table-column prop="consignmentNum" :label="$t('billingPage.consignmentNum')" align="center" min-width="170" />
      <el-table-column
        prop="invoicePrice"
        :label="$t('billingPage.PostageRechargeInvoicePrice')"
        align="center"
        min-width="130"
        sortable="custom"
      >
        <template slot-scope="scope">{{ formatMoney(scope.row.invoicePrice) }}</template>
      </el-table-column>
      <el-table-column prop="originalCsvRawData" :label="$t('billingPage.PostageRechargeRawData')" align="center" min-width="360">
        <template slot-scope="scope">
          <span
            class="raw-data-cell"
            :title="formatRawData(getRawData(scope.row))"
            @click="copyRawData(getRawData(scope.row))"
          >
            {{ formatRawData(getRawData(scope.row)) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.operationText')" align="center" width="150">
        <template slot-scope="scope">
          <el-button
            v-if="String(scope.row.canManualMatch) === '1'"
            type="primary"
            size="mini"
            @click="openManualMatch(scope.row)"
          >
            {{ $t('billingPage.PostageRechargeManualMatch') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <pagination
      v-if="unmatchedRows.length"
      class="table-pagination"
      :total="unmatchedRows.length"
      :page.sync="unmatchedPage"
      :limit.sync="unmatchedPageSize"
      :page-sizes="pageSizes"
      :auto-scroll="false"
      @pagination="handleUnmatchedPagination"
    />

    <ManualMatchDialog ref="ManualMatchDialog" @matched="getData" />
  </div>
</template>

<script>
import viewMiXin from '@/utils/view-mixin.js';
import { billApi } from '@/api';
import { downloadBlob } from '@/utils/index';
import { PAGE_SIZES } from '@/utils/constants';
import ManualMatchDialog from './components/ManualMatchDialog';
import {
  POSTAGE_FORMULAS,
  adaptPostageInvoiceDetail,
  sortNumericRows
} from './postage-ui';

export default {
  name: 'BusinessBillingPostageRechargeDetail',
  components: { ManualMatchDialog },
  mixins: [viewMiXin],
  data() {
    return {
      detail: {
        matchedOrders: [],
        unmatchedItems: []
      },
      unmatchedReason: '',
      reasonSaving: false,
      formulaDefinitions: POSTAGE_FORMULAS,
      activeFormula: null,
      matchedSearchKeyword: '',
      matchedAmountColumns: [
        { prop: 'invoiceAmount', label: this.$t('billingPage.PostageRechargeOrderInvoiceAmount'), width: 145 },
        { prop: 'orderCostReceived', label: this.$t('billingPage.PostageRechargeOrderCostReceived'), width: 175 },
        { prop: 'orderAmountReceived', label: this.$t('billingPage.PostageRechargeOrderAmountReceived'), width: 190 },
        { prop: 'totalOrderCost', label: this.$t('billingPage.PostageRechargeTotalOrderCost'), width: 155 },
        { prop: 'currentDifference', label: this.$t('billingPage.PostageRechargeCurrentDifference'), width: 165, formula: 'currentDifference' },
        { prop: 'currentChargeAmount', label: this.$t('billingPage.PostageRechargeCurrentChargeAmount'), width: 195, formula: 'currentChargeAmount' }
      ],
      pageSizes: PAGE_SIZES,
      matchedPage: 1,
      matchedPageSize: PAGE_SIZES[0],
      matchedSort: {},
      unmatchedPage: 1,
      unmatchedPageSize: PAGE_SIZES[0],
      unmatchedSort: {}
    };
  },
  computed: {
    invoiceNumber() {
      return this.$route.query.invoiceNumber || '';
    },
    fileName() {
      return this.$route.query.fileName || '';
    },
    logisticsCompany() {
      return this.detail.logisticsCompany || this.$route.query.logisticsCompany || '';
    },
    matchedRows() {
      return this.detail.matchedOrders || [];
    },
    filteredMatchedRows() {
      const keyword = this.matchedSearchKeyword.toLowerCase();
      if (!keyword) return this.matchedRows;
      return this.matchedRows.filter(row => {
        return [row.orderNumber, row.trackingNumber].some(value => {
          return String(value || '').toLowerCase().includes(keyword);
        });
      });
    },
    unmatchedRows() {
      return this.detail.unmatchedItems || [];
    },
    sortedMatchedRows() {
      return sortNumericRows(this.filteredMatchedRows, this.matchedSort);
    },
    sortedUnmatchedRows() {
      return sortNumericRows(this.unmatchedRows, this.unmatchedSort);
    },
    matchedPagedRows() {
      return this.getPagedRows(this.sortedMatchedRows, this.matchedPage, this.matchedPageSize);
    },
    unmatchedPagedRows() {
      return this.getPagedRows(this.sortedUnmatchedRows, this.unmatchedPage, this.unmatchedPageSize);
    },
    overviewMetrics() {
      return [
        { key: 'invoice', label: this.$t('billingPage.PostageRechargeInvoiceAmount'), value: this.formatMoney(this.detail.invoiceAmount), className: 'is-invoice' },
        { key: 'matched', label: this.$t('billingPage.PostageRechargeMatchedAmount'), value: this.formatMoney(this.detail.matchedAmount), className: 'is-matched' },
        { key: 'received', label: this.$t('billingPage.PostageRechargeCostReceived'), value: this.formatMoney(this.detail.costReceived), className: 'is-history' },
        { key: 'difference', label: this.$t('billingPage.PostageRechargeDifference'), value: this.formatMoney(this.detail.difference), className: 'is-calculation' },
        { key: 'charge', label: this.$t('billingPage.PostageRechargeCurrentChargeAmount'), value: this.formatMoney(this.detail.currentChargeAmount), className: 'is-charge' },
        { key: 'margin', label: this.$t('billingPage.PostageRechargeAverageProfitMargin'), value: this.formatPercent(this.detail.averageProfitMargin), className: 'is-margin' }
      ];
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      if (!this.invoiceNumber) {
        this.$message.warning(this.$t('billingPage.PostageRechargeInvoiceRequired'));
        return;
      }
      billApi.getPostageRechargeInvoiceDetails({ invoiceNumber: this.invoiceNumber }).then(res => {
        const response = res.data || res;
        this.detail = adaptPostageInvoiceDetail(response);
        this.unmatchedReason = this.detail.unmatchedReason || '';
        this.matchedPage = 1;
        this.unmatchedPage = 1;
      });
    },
    handleBack() {
      this.cancel();
    },
    saveReason() {
      const id = this.detail.id || this.$route.query.id;
      if (!id) {
        this.$message.warning(this.$t('billingPage.PostageRechargeReasonIdRequired'));
        return;
      }
      this.reasonSaving = true;
      billApi.updatePostageRechargeUnmatchedReason({
        id,
        reason: this.unmatchedReason
      }).then(res => {
        if (res.code == 200 || !res.failed) {
          this.detail.unmatchedReason = this.unmatchedReason;
          this.msgSuccess(this.$t('toast.operationSuccessText'));
        }
      }).finally(() => {
        this.reasonSaving = false;
      });
    },
    downloadFile() {
      if (!this.fileName) return;
      billApi.downloadPostageRechargeFile({ fileName: this.fileName }).then(res => {
        downloadBlob(res, this.fileName);
      });
    },
    openManualMatch(row) {
      this.$refs.ManualMatchDialog.show({
        ...row,
        invoicePrice: row.invoicePrice === undefined ? row.totalInvoicePrice : row.invoicePrice
      });
    },
    getRawData(row) {
      return row.originalCsvRawData || row.originalCsvData;
    },
    getPagedRows(rows, page, pageSize) {
      const start = (page - 1) * pageSize;
      return rows.slice(start, start + pageSize);
    },
    handleMatchedPagination({ page, size }) {
      this.matchedPage = page;
      this.matchedPageSize = size;
    },
    handleMatchedSearch() {
      this.matchedPage = 1;
    },
    handleMatchedSortChange({ prop, order }) {
      this.matchedSort = { prop, order };
      this.matchedPage = 1;
    },
    handleUnmatchedSortChange({ prop, order }) {
      this.unmatchedSort = { prop, order };
      this.unmatchedPage = 1;
    },
    handleUnmatchedPagination({ page, size }) {
      this.unmatchedPage = page;
      this.unmatchedPageSize = size;
    },
    formatMoney(value) {
      const number = Number(value || 0);
      return `$ ${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatPercent(value) {
      const number = Number(value || 0);
      return `${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    },
    setActiveFormula(row, prop) {
      const definition = this.formulaDefinitions[prop];
      if (!definition) return;
      this.activeFormula = {
        rowId: row.id,
        resultField: prop,
        fields: definition.fields
      };
    },
    clearActiveFormula() {
      this.activeFormula = null;
    },
    getMatchedCellClass({ row, column }) {
      const prop = column.property;
      const classes = [];
      if (prop) classes.push(`postage-cell--${prop}`);
      if (
        this.activeFormula &&
        this.activeFormula.rowId === row.id &&
        this.activeFormula.fields.includes(prop)
      ) {
        classes.push('postage-cell--linked');
        if (this.activeFormula.resultField === prop) {
          classes.push('postage-cell--result');
        }
      }
      if ((row.validationErrors || []).some(error => error.field === prop)) {
        classes.push('postage-cell--invalid');
      }
      return classes.join(' ');
    },
    copyValue(value) {
      if (!value) return;
      this.$copyText(String(value)).then(() => {
        this.msgSuccess(this.$t('ReplicationSucceededText'));
      });
    },
    showOrderInfo(row) {
      this.$alert(
        `${row.orderNumber} · ${row.trackingNumber}`,
        this.$t('billingPage.PostageRechargeMatchedOrder'),
        { confirmButtonText: this.$t('toast.okText') }
      );
    },
    formatRawData(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      return Object.keys(value).map(key => `${key}: ${value[key]}`).join(' | ');
    },
    copyRawData(value) {
      const text = this.formatRawData(value);
      if (!text) return;
      this.$copyText(text).then(() => {
        this.msgSuccess(this.$t('ReplicationSucceededText'));
      }, () => {
        this.msgError(this.$t('ReplicationFailedText') || 'Copy failed');
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.postage-recharge-detail {
  --postage-invoice: #2457a6;
  --postage-invoice-bg: #f2f6fc;
  --postage-matched: #237a57;
  --postage-matched-bg: #f0f8f4;
  --postage-history: #526273;
  --postage-history-bg: #f5f7fa;
  --postage-calculation: #6552a3;
  --postage-calculation-bg: #e8e2f5;
  --postage-charge: #53439a;
  --postage-charge-bg: #dcd3f0;
  --postage-margin: #24756f;
  --postage-margin-bg: #eef8f7;
  --postage-error: #c45656;
  --postage-error-bg: #fef0f0;

  .detail-overview {
    display: flex;
    align-items: stretch;
    margin-bottom: 20px;

    &__col {
      display: flex;
    }
  }

  .overview-card,
  .reason-card {
    width: 100%;
    height: 100%;
    min-height: 250px;
    padding: 22px 24px;
    border-radius: 6px;
    border: 1px solid #ebeef5;
    background: #fff;
  }

  .overview-card {
    &__meta {
      color: #0056d2;
      font-size: 12px;
      font-weight: 700;
      line-height: 18px;
    }

    &__title {
      margin-top: 8px;
      color: #143161;
      font-size: 30px;
      font-weight: 700;
      line-height: 38px;
    }

    &__sub {
      margin-top: 12px;
      color: #606266;
      font-size: 14px;
    }

    &__amounts {
      margin-top: 30px;
    }
  }

  .overview-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(150px, 1fr));
    gap: 10px;
    margin-top: 24px;

    &__item {
      padding: 10px 12px;
      border-left: 3px solid #dcdfe6;
      border-radius: 3px;
      background: #f8f9fb;

      span,
      strong {
        display: block;
      }

      span {
        color: #7a8491;
        font-size: 11px;
      }

      strong {
        margin-top: 5px;
        color: #303133;
        font-size: 15px;
        font-variant-numeric: tabular-nums;
      }

      &.is-invoice {
        border-left-color: var(--postage-invoice);
      }

      &.is-matched {
        border-left-color: var(--postage-matched);
      }

      &.is-history {
        border-left-color: var(--postage-history);
      }

      &.is-calculation {
        border-left-color: var(--postage-calculation);
      }

      &.is-charge {
        border-left-color: var(--postage-charge);
      }

      &.is-margin {
        border-left-color: var(--postage-margin);
      }
    }
  }

  .reason-card__label {
    color: #606266;
    font-size: 12px;
    font-weight: 600;
  }

  .reason-card__amount {
    margin-top: 8px;
    color: #143161;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    font-variant-numeric: tabular-nums;

    &.is-success {
      color: #006d48;
    }
  }

  .reason-card {
    background: #f5f7ff;

    &__amount {
      margin-bottom: 18px;
      color: #9f403d;
    }
  }

  .divider-count {
    margin-left: 8px;
    color: #606266;
    font-size: 13px;

    &.is-danger {
      color: #9f403d;
    }
  }

  .amount-positive {
    color: #006d48;
    font-weight: 600;
  }

  .amount-negative {
    color: #9f403d;
    font-weight: 600;
  }

  .table-pagination {
    margin-bottom: 18px;
  }

  .table-filter {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 10px;

    &__input {
      width: 360px;
      max-width: 100%;
    }
  }

  .raw-data-cell {
    display: block;
    width: 100%;
    overflow: hidden;
    color: #143161;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .formula-info {
    margin-left: 5px;
    color: #77869a;
    cursor: help;
    outline: none;

    &:focus {
      color: #0056d2;
    }
  }

  .copy-value,
  .amount-button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #0056d2;
    font: inherit;
    cursor: pointer;
  }

  .copy-value {
    text-align: center;
  }

  .amount-button {
    color: var(--postage-charge);
    font-weight: 700;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
  }

  .detail-amount {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .calculation-breakdown {
    &__title {
      margin-bottom: 14px;
      color: #303133;
      font-size: 15px;
      font-weight: 700;
    }

    &__row,
    &__result {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
    }

    &__result {
      margin-top: 4px;
      padding-top: 12px;
      border-top: 1px solid #dcdfe6;
      color: var(--postage-charge);
    }
  }

  ::v-deep .postage-cell--invoiceAmount {
    color: var(--postage-invoice);
  }

  ::v-deep .postage-cell--orderCostReceived,
  ::v-deep .postage-cell--orderAmountReceived {
    color: var(--postage-history);
  }

  ::v-deep .postage-cell--totalOrderCost {
    color: var(--postage-invoice);
  }

  ::v-deep .postage-cell--currentDifference {
    color: var(--postage-calculation);
  }

  ::v-deep .postage-cell--currentChargeAmount {
    color: var(--postage-charge);
  }

  ::v-deep .postage-cell--profitMargin {
    color: var(--postage-margin);
  }

  ::v-deep .postage-cell--linked {
    box-shadow: inset 0 0 0 1px #b7a8dc;
    background: var(--postage-calculation-bg) !important;
  }

  ::v-deep .postage-cell--result {
    box-shadow: inset 0 0 0 2px var(--postage-calculation);
    background: var(--postage-charge-bg) !important;
  }

  ::v-deep .postage-cell--invalid {
    box-shadow: inset 0 0 0 1px var(--postage-error);
    background: var(--postage-error-bg) !important;
  }

  @media (max-width: 1200px) {
    .overview-metrics {
      grid-template-columns: repeat(2, minmax(150px, 1fr));
    }
  }
}
</style>
